import React from 'react';
import { flowRight } from 'lodash/fp';
import { withStyles } from '@material-ui/core/styles';
import {
    FormControl,
    Typography,
    TextField,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
} from '@material-ui/core';

import 'react-phone-number-input/style.css';

import DualListBox from 'ui/duallist/DualListBox';

import { withAppContext } from '../../../AppContext';

const styles = theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    row: {
        width: '100%',
    },
    email: {
        marginBottom: 24,
    },
    roles: {
        marginTop: 10,
        height: 300,
    },
    list: {
        maxHeight: 312,
        overflow: 'auto',
    },
});

const formatCompanyUsers = (companyUsers = [], portalUsers = []) => {
    const existingEmails = portalUsers.map(user => user.email);
    return companyUsers
        .filter(
            user =>
                user.status === 'active' && !existingEmails.includes(user.email)
        )
        .sort((a, b) => {
            if (a.email.toLowerCase() < b.email.toLowerCase()) {
                return -1;
            }
            if (a.email.toLowerCase() > b.email.toLowerCase()) {
                return 1;
            }
            return 0;
        });
};

const getUsernameError = userDetailsErrors => {
    if (userDetailsErrors.partner_portal_username_taken) {
        return 'Username is already taken.';
    }

    if (typeof userDetailsErrors.partner_portal_username === 'string') {
        return userDetailsErrors.partner_portal_username;
    }

    return '';
};

const UserDetailsStep = props => {
    const inputLabel = React.useRef(null);

    const {
        classes,
        userDetails = {},
        userDetailsErrors = {},
        onChange,
        editable,
        canEdit = {},
        additionalData,
        app,
    } = props;

    const { portalUsers = [], companyUsers, roles } = additionalData;
    const isCurrentUser = app.currentUser.uuid === userDetails.uuid;

    const userNameError = getUsernameError(userDetailsErrors);

    const labelWidth = inputLabel.current && inputLabel.current.offsetWidth;
    const users =
        canEdit.email && editable
            ? formatCompanyUsers(companyUsers, portalUsers)
            : portalUsers;
    return (
        <div className={classes.container}>
            <Typography variant="h6">User details</Typography>
            <div className={classes.row}>
                <Typography variant="subtitle1">
                    Choose the user to grant Partner Portal access.
                </Typography>
                <FormControl
                    fullWidth
                    className={classes.email}
                    variant="outlined"
                    error={userDetailsErrors.email}
                    disabled={!canEdit.email || !editable}
                >
                    <InputLabel ref={inputLabel}>Email</InputLabel>
                    <Select
                        value={userDetails.email}
                        onChange={event => {
                            onChange('email', event.target.value);
                        }}
                        MenuProps={{
                            MenuListProps: {
                                className: classes.list,
                            },
                        }}
                        input={
                            <OutlinedInput
                                labelWidth={labelWidth}
                                value={userDetails.email}
                            />
                        }
                    >
                        {users.map(user => (
                            <MenuItem key={user.email} value={user.email}>
                                {user.email}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography variant="subtitle1">
                    This field is only shown for legacy users which have not
                    been migrated to their email
                </Typography>
                <TextField
                    error={
                        userDetailsErrors.partner_portal_username ||
                        userDetailsErrors.partner_portal_username_taken
                    }
                    disabled={true}
                    label="Partner portal username"
                    value={userDetails.partner_portal_username}
                    onChange={event => {
                        onChange('partner_portal_username', event.target.value);
                    }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    inputProps={{ maxLength: 320 }}
                    helperText={userNameError}
                />
            </div>
            <Typography variant="h6">Roles</Typography>
            <div className={classes.row}>
                <Typography variant="subtitle1">
                    Assign at least one role for every user.
                </Typography>
                <DualListBox
                    className={classes.roles}
                    disabled={!canEdit.roles || !editable}
                    options={roles.map(r => {
                        const isCurrentUserAdmin =
                            isCurrentUser && r.name === 'Admin';
                        return {
                            value: r.uuid,
                            label: r.name,
                            selectable: !isCurrentUserAdmin,
                        };
                    })}
                    initialValue={userDetails.roles}
                    sourceListHeaderName="Available roles"
                    destinationListHeaderName="Assigned roles"
                    destinationListError={userDetailsErrors.roles}
                    onChange={values => onChange('roles', values)}
                />
            </div>
        </div>
    );
};

export default flowRight(withStyles(styles), withAppContext)(UserDetailsStep);
